import { useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import staffMemberServices, {
  AddDirectReportProps,
  AddDirectReportResponse,
  CrateJobInfoResponse,
  CreateJobInfoProps,
  ListStaffMembersResponse,
  RemoveDirectReportProps,
  UpdateJobInfoProps,
  UpdateJobInfoResponse,
} from '@/services/staffMember';
import { FetchApiIds } from '@/types';
import StaffMemberJobItemComponent, {
  Props as StaffMemberJobItemComponentProps,
  FormData,
} from './StaffMemberJobItem.component';

type Props = Pick<
  StaffMemberJobItemComponentProps,
  'teamsList' | 'jobInfo' | 'onAddCancel' | 'isEditing' | 'onEditClick' | 'onEditCancel' | 'id'
> & {
  staffMemberId: string;
  onAddNewJob?: (jobInfo: CrateJobInfoResponse) => void;
  onUpdateJob?: (response: UpdateJobInfoResponse) => void;
  onDeleteJob?: (id: string) => void;
  onRemoveDirectReport?: () => void;
  onAddDirectReport?: () => void;
};

const StaffMemberJobItem = ({
  staffMemberId,
  onAddNewJob,
  jobInfo,
  onUpdateJob,
  onDeleteJob,
  onRemoveDirectReport,
  onAddDirectReport,
  ...componentProps
}: Props): JSX.Element => {
  const [leaderSearch, setLeaderSearch] = useState<string>('');
  const [reportersSearch, setReportersSearch] = useState<string>('');
  const [isRemovingReporter, setIsRemovingReporter] = useState<string>();
  const toast = useToastContext();

  const leaderSearchResponse = useApiFetch<ListStaffMembersResponse>(
    [FetchApiIds.LIST_STAFF_MEMBERS, leaderSearch],
    async () =>
      staffMemberServices.listStaffMembers({
        take: 10,
        page: 1,
        term: leaderSearch,
      }),
    { enabled: leaderSearch.length >= 2 }
  );

  const reportersSearchResponse = useApiFetch<ListStaffMembersResponse>(
    [FetchApiIds.LIST_STAFF_MEMBERS, reportersSearch],
    async () =>
      staffMemberServices.listStaffMembers({
        take: 10,
        page: 1,
        term: reportersSearch,
      }),
    { enabled: reportersSearch.length >= 2 }
  );

  const createJobInfoMutation = useApiSubmit<CrateJobInfoResponse, CreateJobInfoProps>(
    staffMemberServices.createJobInfo
  );

  const updateJobInfoMutation = useApiSubmit<UpdateJobInfoResponse, UpdateJobInfoProps>(
    staffMemberServices.updateJobInfo
  );

  const removeReporterMutation = useApiSubmit<never, RemoveDirectReportProps>(
    staffMemberServices.removeDirectReport
  );

  const addReporterMutation = useApiSubmit<AddDirectReportResponse, AddDirectReportProps>(
    staffMemberServices.addDirectReport
  );

  const handleSubmit = async (data: FormData): Promise<void> => {
    try {
      if (jobInfo) {
        const updatedJobInfo = await updateJobInfoMutation.mutateAsync({
          staffMemberId,
          jobInfoId: jobInfo.id,
          title: data.title,
          seniority: data.seniority,
          area: data.area,
          teamId: data.teamId,
          directLeaderId: data.directLeaderId,
          fromDate: data.fromDate,
          toDate: data.toDate,
        });
        toast.setMessage('Job updated successfully.');
        toast.setSeverity('success');
        toast.showToast();
        onUpdateJob?.(updatedJobInfo);
      } else {
        const newJobInfo = await createJobInfoMutation.mutateAsync({
          staffMemberId,
          title: data.title,
          seniority: data.seniority,
          area: data.area,
          teamId: data.teamId,
          directLeaderId: data.directLeaderId,
          directReportsIds: data.directReportsIds,
          fromDate: data.fromDate,
          toDate: data.toDate,
        });
        toast.setMessage('Job created successfully.');
        toast.setSeverity('success');
        toast.showToast();
        onAddNewJob?.(newJobInfo);
      }
    } catch (error) {
      toast.setMessage(`There was an error ${jobInfo ? 'updating' : 'creating'} the job.`);
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  const handleRemoveReporterClick = async ({
    userId,
    ...data
  }: Omit<RemoveDirectReportProps, 'staffMemberId'> & {
    userId: string;
  }): Promise<void> => {
    try {
      setIsRemovingReporter(userId);
      await removeReporterMutation.mutateAsync({
        staffMemberId,
        ...data,
      });
      toast.setMessage('Direct reporter removed successfully.');
      toast.setSeverity('success');
      toast.showToast();
      onRemoveDirectReport?.();
    } catch {
      toast.setMessage(`There was an error removing the direct reporter.`);
      toast.setSeverity('error');
      toast.showToast();
    } finally {
      setIsRemovingReporter(undefined);
    }
  };

  const handleAddReporterClick = async ({
    staffMemberId: directReportId,
    jobInfoId,
  }: {
    staffMemberId: string;
    jobInfoId: string;
  }): Promise<void> => {
    try {
      await addReporterMutation.mutateAsync({
        ownerStaffMemberId: staffMemberId,
        directReportId,
        jobInfoId,
      });
      toast.setMessage('Direct reporter added successfully.');
      toast.setSeverity('success');
      toast.showToast();
      onAddDirectReport?.();
    } catch {
      toast.setMessage(`There was an error adding the direct reporter.`);
      toast.setSeverity('error');
      toast.showToast();
    } finally {
      setIsRemovingReporter(undefined);
    }
  };

  return (
    <StaffMemberJobItemComponent
      leaderSearch={leaderSearch}
      onLeaderSearch={setLeaderSearch}
      leadersFound={leaderSearchResponse?.data?.data}
      reportersSearch={reportersSearch}
      onReportersSearch={setReportersSearch}
      reportersFound={reportersSearchResponse?.data?.data}
      onSubmit={handleSubmit}
      isLoading={createJobInfoMutation.isLoading || updateJobInfoMutation.isLoading}
      jobInfo={jobInfo}
      onRemoveReporterClick={handleRemoveReporterClick}
      isRemovingReporter={isRemovingReporter}
      onAddReporterClick={handleAddReporterClick}
      onDeleteJob={onDeleteJob}
      {...componentProps}
    />
  );
};

export default StaffMemberJobItem;
